/**
 * Utility functions for extracting and searching references from extracted_text
 */

/**
 * Parse comma-separated references into an array
 * @param {string} referenceString - Comma-separated references (e.g., "Section 1.2, Article 5, Clause 3.4")
 * @returns {Array<string>} - Array of individual references
 */
export const parseReferences = (referenceString) => {
    if (!referenceString || referenceString === 'N/A' || referenceString === 'Not specified') {
        return [];
    }

    return referenceString
        .split(',')
        .map(ref => ref.trim())
        .filter(ref => ref.length > 0);
};

/**
 * Search for a specific reference in the extracted text
 * @param {string} reference - The reference to search for (e.g., "Section 1.2")
 * @param {string} extractedText - The full extracted text from the document
 * @returns {Object} - { found: boolean, content: string, context: string }
 */
export const findReferenceInText = (reference, extractedText) => {
    if (!reference || !extractedText) {
        return { found: false, content: '', context: '' };
    }

    const normalizedRef = reference.toLowerCase().trim();
    const text = extractedText;
    const textLower = text.toLowerCase();

    // Try to find the reference with various patterns
    const patterns = [
        new RegExp(`${escapeRegex(normalizedRef)}[:\\s]([^\\n]{0,500})`, 'i'),
        new RegExp(`\\b${escapeRegex(normalizedRef)}\\b[.:\\s-]*([^\\n]{0,500})`, 'i'),
        new RegExp(`(?:^|\\n)\\s*${escapeRegex(normalizedRef)}[.:\\s-]*([^\\n]{0,500})`, 'i'),
    ];

    for (const pattern of patterns) {
        const match = text.match(pattern);
        if (match) {
            const startIdx = match.index;
            const endIdx = Math.min(startIdx + 1000, text.length);
            
            // Get surrounding context
            const contextStart = Math.max(0, startIdx - 200);
            const contextEnd = Math.min(text.length, endIdx + 200);
            const context = text.substring(contextStart, contextEnd).trim();
            
            // Extract the main content
            const content = match[1] ? match[1].trim() : text.substring(startIdx, endIdx).trim();

            return {
                found: true,
                content,
                context,
                matchedPattern: pattern.source
            };
        }
    }

    // Fallback: Try to find the reference anywhere in the text
    const refIndex = textLower.indexOf(normalizedRef);
    if (refIndex !== -1) {
        const contextStart = Math.max(0, refIndex - 200);
        const contextEnd = Math.min(text.length, refIndex + 800);
        const context = text.substring(contextStart, contextEnd).trim();

        return {
            found: true,
            content: context,
            context,
            matchedPattern: 'fallback'
        };
    }

    return { found: false, content: '', context: '' };
};

/**
 * Search for multiple references in the extracted text
 * @param {Array<string>} references - Array of references to search for
 * @param {string} extractedText - The full extracted text from the document
 * @returns {Array<Object>} - Array of { reference, found, content, context }
 */
export const findMultipleReferences = (references, extractedText) => {
    return references.map(reference => ({
        reference,
        ...findReferenceInText(reference, extractedText)
    }));
};

/**
 * Extract all references from analysis data and search in extracted_text
 * @param {Object} analysisData - The analysis response data
 * @param {string} extractedText - The full extracted text from the document
 * @returns {Object} - Map of question keys to their reference data
 */
export const extractAllReferences = (analysisData, extractedText) => {
    if (!analysisData || !extractedText) {
        return {};
    }

    const referenceMap = {};

    Object.keys(analysisData).forEach(key => {
        if (key.startsWith('Question')) {
            const answerData = analysisData[key];
            const referenceString = Array.isArray(answerData) && answerData[1] ? answerData[1] : null;

            if (referenceString) {
                const parsedRefs = parseReferences(referenceString);
                const foundRefs = findMultipleReferences(parsedRefs, extractedText);
                
                referenceMap[key] = {
                    original: referenceString,
                    parsed: parsedRefs,
                    details: foundRefs
                };
            }
        }
    });

    return referenceMap;
};

/**
 * Escape special regex characters
 * @param {string} string - String to escape
 * @returns {string} - Escaped string
 */
const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Format reference content for display
 * @param {string} content - Raw content from the document
 * @param {number} maxLength - Maximum length to display
 * @returns {string} - Formatted content
 */
export const formatReferenceContent = (content, maxLength = 500) => {
    if (!content) return '';
    
    let formatted = content.trim();
    
    // Remove excessive whitespace
    formatted = formatted.replace(/\s+/g, ' ');
    
    // Truncate if too long
    if (formatted.length > maxLength) {
        formatted = formatted.substring(0, maxLength) + '...';
    }
    
    return formatted;
};

/**
 * Highlight search term in text
 * @param {string} text - The text to highlight in
 * @param {string} searchTerm - The term to highlight
 * @returns {string} - HTML string with highlighted terms
 */
export const highlightSearchTerm = (text, searchTerm) => {
    if (!text || !searchTerm) return text;
    
    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    return text.replace(regex, '<mark class="bg-yellow-200">$1</mark>');
};
